import type { ColorScheme } from './palette';

export type TextSize = 'small' | 'medium' | 'large';
export type ThemePreference = 'light' | 'dark' | 'system';
export type WidthPreference = 'standard' | 'wide';

export interface Appearance {
  textSize: TextSize;
  theme: ThemePreference;
  width: WidthPreference;
  reduceMotion: boolean;
}

/** What the OS asks for (prefers-color-scheme, prefers-reduced-motion). */
export interface SystemSettings {
  prefersDark: boolean;
  prefersReducedMotion: boolean;
}

/** Text size multipliers (spec §2). */
export const TEXT_SCALE: Record<TextSize, number> = {
  small: 0.9,
  medium: 1,
  large: 1.15,
};

const TEXT_SIZE_ATTR: Record<TextSize, string> = {
  small: 's',
  medium: 'm',
  large: 'l',
};

export const resolveColorScheme = (
  theme: ThemePreference,
  system: SystemSettings,
): ColorScheme =>
  theme === 'system' ? (system.prefersDark ? 'dark' : 'light') : theme;

/** Motion is reduced when the app preference or the OS asks for it. */
export const resolveReducedMotion = (
  appearance: Pick<Appearance, 'reduceMotion'>,
  system: SystemSettings,
): boolean => appearance.reduceMotion || system.prefersReducedMotion;

/**
 * Write the appearance onto <html>: data-theme (resolved light/dark),
 * data-text-size (s/m/l, which sets the root font size in tokens.css),
 * data-width and data-reduced-motion.
 */
export const applyAppearance = (
  appearance: Appearance,
  system: SystemSettings,
  root: HTMLElement = document.documentElement,
): { scheme: ColorScheme; reducedMotion: boolean } => {
  const scheme = resolveColorScheme(appearance.theme, system);
  const reducedMotion = resolveReducedMotion(appearance, system);
  root.dataset.theme = scheme;
  root.dataset.themePreference = appearance.theme;
  root.dataset.textSize = TEXT_SIZE_ATTR[appearance.textSize];
  root.dataset.width = appearance.width;
  if (reducedMotion) {
    root.dataset.reducedMotion = '1';
  } else {
    delete root.dataset.reducedMotion;
  }
  return { scheme, reducedMotion };
};

const query = (q: string) =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(q)
    : null;

export const readSystemSettings = (): SystemSettings => ({
  prefersDark: !!query('(prefers-color-scheme: dark)')?.matches,
  prefersReducedMotion: !!query('(prefers-reduced-motion: reduce)')?.matches,
});

/** Call `onChange` whenever the OS colour scheme or motion setting changes. */
export const watchSystemSettings = (onChange: () => void): (() => void) => {
  const lists = [
    query('(prefers-color-scheme: dark)'),
    query('(prefers-reduced-motion: reduce)'),
  ].filter((l): l is MediaQueryList => !!l);
  lists.forEach(l => l.addEventListener?.('change', onChange));
  return () => lists.forEach(l => l.removeEventListener?.('change', onChange));
};
